import { KeyboardArrowDownRounded } from '@streamelements/frontend-icons';
import {
  ComboBoxAdornment,
  ComboBoxContent,
  ComboBoxOption,
  ComboBoxRoot,
  ComboBoxTrigger,
} from '../../../components/Combobox';
import { useEffect, useRef, useState } from 'react';

type Resolution = `${number}x${number}`;

const resolutionList: Resolution[] = ['1080x1920', '1920x1080', '2560x1440', '1280x720', '3840x2160'];
const resolutionLabels: string[] = ['(1080p Vertical)', '(1080p)', '(1440p)', '(720p)', '(4k)'];
const resolutions: Map<Resolution, string> = new Map(
  resolutionList.map((resolution, ind) => [resolution, `${resolution} ${resolutionLabels[ind]}`])
);

export function ResolutionComboBox({
  onValueChange,
  value,
  disabled,
}: {
  onValueChange: ({ width, height }: { width: number; height: number }) => void;
  value: Resolution;
  disabled?: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [customResolution, setCustomResolution] = useState<Resolution | null>(() =>
    !resolutions.has(value as Resolution) ? value : null
  );

  function handleValueChange(resolution: Resolution) {
    const [width, height] = resolution.split('x').map(Number);
    onValueChange?.({
      width: width,
      height: height,
    });
  }

  function handleInputValueChange(inputValue: string) {
    if (/^\d+x\d+$/.test(inputValue) && !resolutions.has(inputValue as Resolution)) {
      setCustomResolution(inputValue as Resolution);
      handleValueChange(inputValue as Resolution);
    } else {
      setCustomResolution(null);
    }
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = resolutions.get(value as Resolution) ?? value;
    }
  }, [value]);

  return (
    <ComboBoxRoot
      value={[value]}
      onValueChange={([next]) => handleValueChange(next as Resolution)}
      defaultInputValue={resolutions.get(value as Resolution) ?? value}
      onInputValueChange={handleInputValueChange}
    >
      <ComboBoxTrigger label="Resolution" ref={inputRef} disabled={disabled}>
        <ComboBoxAdornment>
          <KeyboardArrowDownRounded />
        </ComboBoxAdornment>
      </ComboBoxTrigger>
      <ComboBoxContent>
        {customResolution && <ComboBoxOption value={customResolution}>{customResolution}</ComboBoxOption>}

        {Array.from(resolutions.entries()).map(([value, label]) => (
          <ComboBoxOption key={value} value={value} label={label} disabled={disabled}>
            {label}
          </ComboBoxOption>
        ))}
      </ComboBoxContent>
    </ComboBoxRoot>
  );
}
